import { navItems } from '../../_nav';
import { appRoutes } from '../../shared/routes/app-routes';
import { UserActions, canCreate, canSeeMenuItem } from '../menu-permission';

export interface ScreenEntry {
  title: string;
  module: string;
  icon: string;
  route: string[];
  keywords: string[];
  shortcut?: boolean;
}

export interface ScreenMatch {
  entry: ScreenEntry;
  rank: number;
}

const MAX_RESULTS = 6;

/** Sinônimos por URL da sidebar, para achar a tela pelo termo que o usuário usa no dia a dia. */
const KEYWORDS: Record<string, string[]> = {
  [appRoutes.taxControl.list().join('/')]: ['nfse', 'nfce', 'nota fiscal', 'fiscal', 'imposto'],
  [appRoutes.closures.list().join('/')]: ['fechamentos', 'repasse do evento'],
  [appRoutes.refunds.list().join('/')]: ['reembolso', 'estorno', 'devolucao'],
  [appRoutes.reconciliations.list().join('/')]: ['conciliar', 'adquirente', 'transacoes'],
  [appRoutes.fees.list().join('/')]: ['taxa', 'tarifa'],
  [appRoutes.permissions.list().join('/')]: ['perfil', 'papel', 'acesso', 'role'],
  [appRoutes.appPermissions.list().join('/')]: ['aplicativo', 'perfil do app'],
  [appRoutes.apiAccess.list().join('/')]: ['token', 'integracao', 'chave'],
  [appRoutes.devices.list().join('/')]: ['maquininha', 'pdv', 'totem', 'serial'],
  [appRoutes.physicalDevices.list().join('/')]: ['maquininha', 'serial', 'equipamento'],
  [appRoutes.rentals.list().join('/')]: ['aluguel', 'locacao', 'equipamento'],
  [appRoutes.salePoints.list().join('/')]: ['pdv', 'caixa', 'ponto de venda'],
  [appRoutes.events.list().join('/')]: ['festival', 'show', 'evento'],
  [appRoutes.orders.list().join('/')]: ['pedido', 'venda'],
  [appRoutes.revenueGoals.list().join('/')]: ['meta', 'faturamento'],
  [appRoutes.notifications.list().join('/')]: ['aviso', 'alerta'],
  [appRoutes.pendencies.list().join('/')]: ['pendente', 'tarefa'],
  [appRoutes.advances.list().join('/')]: ['antecipacao', 'adiantamento'],
  [appRoutes.transfers.list().join('/')]: ['repasse', 'transferencia', 'pix']
};

/** Atalhos de criação: aparecem só para quem tem a ação `create` do módulo. */
const CREATE_SHORTCUTS = [
  { title: 'Novo cliente', module: 'Comercial', permission: 'CLIENT', route: appRoutes.clients.new() },
  { title: 'Novo evento', module: 'Operacional', permission: 'EVENT', route: appRoutes.events.new() },
  { title: 'Novo produto', module: 'Operacional', permission: 'PRODUCT', route: appRoutes.products.new() },
  { title: 'Novo usuário', module: 'Administrativo', permission: 'USER', route: appRoutes.users.new() },
  { title: 'Nova organização', module: 'Administrativo', permission: 'ORGANIZATION', route: appRoutes.organizations.new() },
  { title: 'Nova permissão', module: 'Administrativo', permission: 'ROLE', route: appRoutes.permissions.new() }
];

/** Telas mostradas quando a busca está vazia. */
const POPULAR_ROUTES = [
  appRoutes.events.list(),
  appRoutes.clients.list(),
  appRoutes.products.list(),
  appRoutes.closures.list(),
  appRoutes.refunds.list(),
  appRoutes.users.list()
].map((route) => route.join('/'));

export function normalizeText(value: string): string {
  return (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function buildScreenCatalog(menuPermissions: string[], userActions: UserActions): ScreenEntry[] {
  const screens: ScreenEntry[] = [];

  for (const module of navItems) {
    for (const child of module.children || []) {
      if (!canSeeMenuItem(child.permission, menuPermissions, userActions)) continue;
      screens.push({
        title: child.name,
        module: module.name,
        icon: child.icon,
        route: [child.url],
        keywords: KEYWORDS[child.url] || []
      });
    }
  }

  for (const shortcut of CREATE_SHORTCUTS) {
    if (!canCreate(shortcut.permission, userActions)) continue;
    screens.push({
      title: shortcut.title,
      module: shortcut.module,
      icon: 'plus',
      route: shortcut.route,
      keywords: ['criar', 'cadastrar', 'adicionar', 'novo'],
      shortcut: true
    });
  }

  return screens;
}

export function popularScreens(catalog: ScreenEntry[]): ScreenEntry[] {
  return POPULAR_ROUTES
    .map((url) => catalog.find((entry) => !entry.shortcut && entry.route[0] === url))
    .filter((entry): entry is ScreenEntry => !!entry);
}

/**
 * rank 0: o título começa com o termo; 1: alguma palavra do título começa com o termo;
 * 2: o termo está dentro do título; 3: bate só no módulo ou em sinônimos.
 */
export function searchScreens(term: string, catalog: ScreenEntry[]): ScreenMatch[] {
  const normalized = normalizeText(term);
  if (!normalized) return [];

  const tokens = normalized.split(/\s+/);

  return catalog
    .map((entry, order) => ({ entry, order, rank: rankOf(entry, normalized, tokens) }))
    .filter((match) => match.rank !== null)
    .sort((a, b) => a.rank! - b.rank! || a.order - b.order)
    .slice(0, MAX_RESULTS)
    .map(({ entry, rank }) => ({ entry, rank: rank! }));
}

function rankOf(entry: ScreenEntry, term: string, tokens: string[]): number | null {
  const title = normalizeText(entry.title);
  const haystack = [title, normalizeText(entry.module), ...entry.keywords.map(normalizeText)].join(' ');

  const words = haystack.split(/\s+/);
  const matches = (token: string) => words.some((word) => word.startsWith(token)) || (token.length >= 3 && haystack.includes(token));
  if (!tokens.every(matches)) return null;
  if (title.startsWith(term)) return 0;
  if (tokens.every((token) => title.split(/\s+/).some((word) => word.startsWith(token)))) return 1;
  if (tokens.every((token) => title.includes(token))) return 2;
  return 3;
}
